import { Box, Tab, TabList, TabPanel, TabPanels, Tabs } from '@chakra-ui/react';
import React from 'react';

import type { DocTabItem } from '../../config';
import { useCodingScreen } from '../../interface';
import { readState } from '../../slots/readState';
import {
  STRIP_BG,
  STRIP_BORDER,
  STRIP_H,
  STRIP_PAD_X,
  TAB_ACTIVE_BG,
  TAB_ACTIVE_BORDER,
  TAB_BORDER_STYLE,
  TAB_BORDER_WIDTH,
  TAB_FONT_SIZE,
  TAB_GAP,
  TAB_PAD_X,
  TAB_PAD_Y,
  TAB_RADIUS,
  TAB_WEIGHT,
  TAB_WEIGHT_ACTIVE
} from '../../styles/tokens';
import { Slot } from '../Slot';
import { useTabSelection } from '../TabSelectionContext';

/** Ascending by `order`, dropping any tab marked `visible:false` -- same ordering/visibility contract
 *  as PatientSummaryBar/CaseContextBar's `visibleFieldsInOrder`, kept local to this molecule folder
 *  per that precedent (each molecule folder stays self-contained). `.filter` on a `readonly
 *  DocTabItem[]` already returns a fresh mutable array, so `.sort` never mutates the config's own
 *  (readonly, `as const`) tab list. */
function visibleTabsInOrder(tabs: readonly DocTabItem[]): DocTabItem[] {
  return tabs.filter((tab) => tab.visible).sort((a, b) => a.order - b.order);
}

/**
 * LEFT-pane document tab strip. `left.tabs` is `[]` in
 * `DEFAULT_CODING_SCREEN_CONFIG` so by default this renders an empty, labelled
 * `role="region"` container with NO tabs at all -- "Default empty shell OMITS tabs
 * entirely (no inert/disabled tabs without panels)". When a config supplies tabs, each visible one
 * renders as a Chakra `Tab` in a `TabList`, paired POSITIONALLY with an (empty, shell-only) `TabPanel`
 * in `TabPanels` -- Chakra pairs a `Tab` with its `TabPanel` by mount ORDER within each list, not by
 * id, so both lists map over the SAME sorted/filtered `visibleTabs` array here, guaranteeing a Tab and
 * its TabPanel can never drift out of correspondence. This satisfies both the atom-reuse requirement
 * and "a Tab needs a matching TabPanel" a11y rule at once.
 *
 * Each `TabPanel` is an empty shell-only placeholder (real content lives in the sibling
 * `ClinicalDocumentViewer`, not here) -- but Chakra 2.3.7's default Tabs base style still gives the
 * active `TabPanel` `p:4`, which would spill roughly 32px below this strip's fixed height
 * (`STRIP_H`, styles/tokens.ts). Zeroing each panel's padding removes that content height, and
 * clipping the outer `Tabs` root with `overflow:hidden` at its fixed height is the
 * belt-and-suspenders guarantee that nothing inside can ever grow the strip, without touching
 * Chakra's normal selected/non-selected panel `hidden`-attribute wiring (so the tab<->panel pairing
 * above is untouched).
 *
 * CONTROLLED SELECTION: `<Tabs>` is now driven by `useTabSelection()`
 * (scoped per pane by the ancestor `TabSelectionProvider` -- `organisms/DocumentationPane`) instead
 * of Chakra's own uncontrolled internal state -- `index` is the position of the currently-selected
 * `contentKey` within `visibleTabs` (falling back to `0`, i.e. the first tab, when nothing is
 * selected yet or the selection doesn't match any currently-visible tab), and `onChange` calls
 * `select` with the newly-clicked tab's `contentKey`. The clicked tab's actual CONTENT still doesn't
 * render here -- `TabPanel`s stay empty shells -- `ClinicalDocumentViewer` reads the same
 * `useTabSelection()` value to render the selected tab's slot in the pane BODY.
 *
 * INJECTED SLOT: when `docNav.componentId` is set (the coding default binds it to
 * `'NoteNav'`), the whole strip hands off to the resolved `Slot` instead of rendering the static tab
 * strip below -- `docNav` IS the `Slot` item here (a `DataContainerMarker`-shaped container, same
 * pattern `ClinicalDocumentViewer` already uses for its own `viewer` container), bound to its own
 * `contentId`. This container gets NO `data-slot-key` (that attribute names an orderable list ITEM,
 * not a container's own single bound datum -- same rule `ClinicalDocumentViewer` documents for
 * `viewer`). `minH` (not a fixed `h`) lets an injected note-nav list grow past a single tab strip's
 * height instead of clipping.
 *
 * The slot wrapper is `display="flex" alignItems="center"` -- matching `ReviewTransactionsTabs`'
 * container, which has always been a centering flex. As a plain BLOCK box with only `minH`, an
 * injected `NoteNav` sat flush against the strip's TOP edge and every pixel of slack in the band fell
 * below it, so the left strip's tabs read as badly off-center while the right strip's looked correct.
 * Centering here rather than inside `NoteNav` keeps the geometry the SHELL owns (the band's height and
 * how a slot is seated in it) out of the injected component, which cannot see `STRIP_H` at all.
 *
 * TAB TYPE + SPACING: the `Tab`s take their size/weight/padding from `styles/tokens.ts`'s `TAB_*`
 * -- the SAME tokens `NoteNav`'s document tabs use, so a static tab here is visually the same object
 * as an injected note tab: 12px label, 500 resting / 600 selected, a 1px rounded (`TAB_RADIUS`)
 * outline that is `transparent` at rest and `TAB_ACTIVE_BORDER` when selected, and a `TAB_ACTIVE_BG`
 * fill when selected. Chakra's own defaults are all displaced: an unstyled `Tab` is size `md` (`1rem`
 * = 14px on the app's forced 14px root, two px over the prototype's 12px) and sets NO `fontWeight`, so
 * selection carried no type cue at all.
 *
 * The `line` variant's UNDERLINE is deliberately suppressed -- `TabList` gets `borderBottom="none"`
 * (the variant puts a 2px rule there, which stacked with this strip's own 1px `STRIP_BORDER` hairline
 * to paint a double line) and the `Tab`'s `border`/`borderColor` props displace the variant's
 * `borderBottom: 2px solid` + `_selected: { borderColor: currentColor }`. The pill fill carries
 * selection on its own, so an underline UNDER a filled pill would be a doubled cue.
 *
 * The border is set with LONGHANDS (`borderWidth`/`borderStyle`/`borderColor`), never a
 * `border: '1px solid'` shorthand -- see `TAB_BORDER_WIDTH`'s own comment in styles/tokens.ts for why
 * the shorthand painted a black border on every unselected tab. `borderBottomWidth` is then set
 * explicitly on top of `borderWidth` because the `line` variant's `borderBottom: 2px solid` is itself a
 * per-side longhand: whether the all-sides `borderWidth` beats it depends on emotion key order, so the
 * matching per-side prop pins the bottom edge to 1px regardless. Note this is NOT the prototype's
 * `.doc-tab`, which is a flush underline tab; matching `NoteNav` was chosen over matching the prototype
 * so the shell has one tab idiom instead of two.
 */
export const DocumentTitleNav: React.FC = () => {
  const { config, state } = useCodingScreen();
  const { docNav, tabs } = config.left;
  const visibleTabs = visibleTabsInOrder(tabs);
  const { selectedContentKey, select } = useTabSelection();

  if (docNav.componentId !== undefined) {
    const value = readState(state, docNav.contentId);
    return (
      <Box
        role="region"
        aria-label="Document navigation"
        data-content-key={docNav.contentKey}
        display="flex"
        alignItems="center"
        flexShrink={0}
        minH={STRIP_H}
        bg={STRIP_BG}
        borderBottom={`1px solid ${STRIP_BORDER}`}
      >
        <Slot
          item={docNav}
          value={value}
          mode={config.mode}
          fallback={() => null}
        />
      </Box>
    );
  }

  if (visibleTabs.length === 0) {
    return (
      <Box
        role="region"
        aria-label="Document navigation"
        data-content-key={docNav.contentKey}
        flexShrink={0}
        h={STRIP_H}
        minH={STRIP_H}
        bg={STRIP_BG}
        borderBottom={`1px solid ${STRIP_BORDER}`}
      />
    );
  }

  const index = Math.max(
    0,
    visibleTabs.findIndex((tab) => tab.contentKey === selectedContentKey)
  );

  return (
    <Tabs
      role="region"
      aria-label="Document navigation"
      data-content-key={docNav.contentKey}
      flexShrink={0}
      h={STRIP_H}
      minH={STRIP_H}
      bg={STRIP_BG}
      borderBottom={`1px solid ${STRIP_BORDER}`}
      style={{ overflow: 'hidden' }}
      index={index}
      onChange={(i) => select(visibleTabs[i].contentKey)}
    >
      <TabList borderBottom="none" px={STRIP_PAD_X} gap={TAB_GAP}>
        {visibleTabs.map((tab) => (
          <Tab
            key={tab.contentKey}
            data-slot-key={tab.contentKey}
            px={TAB_PAD_X}
            py={TAB_PAD_Y}
            fontSize={TAB_FONT_SIZE}
            fontWeight={TAB_WEIGHT}
            borderWidth={TAB_BORDER_WIDTH}
            borderBottomWidth={TAB_BORDER_WIDTH}
            borderStyle={TAB_BORDER_STYLE}
            borderColor="transparent"
            borderRadius={TAB_RADIUS}
            _selected={{
              fontWeight: TAB_WEIGHT_ACTIVE,
              bg: TAB_ACTIVE_BG,
              borderColor: TAB_ACTIVE_BORDER
            }}
          >
            {tab.label}
          </Tab>
        ))}
      </TabList>
      <TabPanels>
        {visibleTabs.map((tab) => (
          <TabPanel key={tab.contentKey} style={{ padding: 0 }} />
        ))}
      </TabPanels>
    </Tabs>
  );
};
